// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadWarehouses from "@insite/client-framework/Store/Components/FindLocationModal/Handlers/LoadWarehouses";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWarehousesDataView } from "@insite/client-framework/Store/Data/Warehouses/WarehousesSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import DynamicDropdown, { OptionObject } from "@insite/mobius/DynamicDropdown";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { resolveColor, safeColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import useExtractCartLineConfigurableChildrenToArray from "@store/Framework/Common/GoogleAnalytics/hooks/useExtractCartLineConfigurableChildrenToArray";
import getAvailabilityByWarehouse from "@store/Framework/Store/Store/Pages/CheckoutReviewAndSubmit/Handlers/GetAvailabilityByWarehouse";
import setWarehouseOverride from "@store/Framework/Store/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetWarehouseOverride";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    const { cartId } = state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    return {
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        showPricing: state.context.showPricing,
        isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
        warehouseOverride: state.store.pages.checkoutReviewAndSubmit.warehouseOverride,
        availabilityByWarehouse: state.store.pages.checkoutReviewAndSubmit.availabilityByWarehouse,
        pickUpWarehouse: state.context.session.pickUpWarehouse,
        fulfillmentMethod: cart?.fulfillmentMethod || session.fulfillmentMethod,
        warehousesDataView: getWarehousesDataView(state, state.components.findLocationModal.getWarehousesParameter),
        canViewDropdown: isAuthenticated && (session.userRoles || "").indexOf(constants.roles.pointOfSale) !== -1,
        isCSOrder: state.store.pages.checkoutReviewAndSubmit.isCSOrder,
    };
};

const mapDispatchToProps = { setWarehouseOverride, getAvailabilityByWarehouse, loadWarehouses };

interface OwnProps {
    cartLines: CartLineModel[];
    extendedStyles?: WarehouseOverrideDropdownStyles;
    overridedStyles?: WarehouseOverrideDropdownStyles;
}
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface WarehouseOverrideDropdownStyles {
    wrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    warehouseName?: TypographyPresentationProps;
    stockAsciiIcon?: TypographyPresentationProps;
}

export const warehouseOverrideDropdownStyles: WarehouseOverrideDropdownStyles = {
    wrapper: {
        css: css`
            margin-bottom: 2em;
        `,
    },
    warehouseName: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.large};
            font-weight: ${x => x.theme.design.typography.weight.semibold};
        `,
    },
    stockAsciiIcon: {
        css: css`
            font-family: icons, sans-serif;
            font-weight: ${x => x.theme.design.typography.weight.base};
            font-style: normal;
            font-variant: normal;
            text-transform: none;
            line-height: 1;
            margin-right: 0.4em;
        `,
    },
    label: {
        variant: "legend",
        css: css`
            ${x => x.theme.typography.legend.css}
            display: block;
        `,
    },
};

const iconCheck = "&#59657;";
const iconX = "&#59658;";
const iconNotAvailable = "&#59679;";
const getAvailabilityColor = (availability?: string) => {
    const availabilityType = availability;
    if (availabilityType === "Available" || availabilityType === "AllAvailable") {
        return "success";
    }

    if (availabilityType === "AvailableSoon") {
        return "warning";
    }

    if (availabilityType === "PartiallyAvailable") {
        return "warning";
    }

    if (availabilityType === constants.availability.notAvailable) {
        return "danger";
    }

    if (availabilityType === "Restricted") {
        return "text";
    }

    return "success";
};

const getAvailabilityIcon = (availability: string) => {
    const availabilityType = availability;
    if (availabilityType === "Available" || availabilityType === "AllAvailable") {
        return iconCheck;
    }

    if (availabilityType === "NotAvailable") {
        return iconCheck;
    }

    if (availabilityType === "PartiallyAvailable") {
        return iconCheck;
    }
    if (availabilityType === "AvailableSoon") {
        return iconCheck;
    }

    if (availabilityType === constants.availability.notAvailable) {
        return iconCheck;
    }

    if (availabilityType === "Restricted") {
        return iconX;
    }

    return iconCheck;
};

const InventoryMessage = styled.div<{ color: string }>`
    color: ${props => safeColor(resolveColor(props.color, props.theme)).rgb().string()};
`;

const WarehouseOverrideDropdown: FC<Props> = ({
    cartLines,
    canViewDropdown,
    pickUpWarehouse,
    fulfillmentMethod,
    warehouseOverride,
    extendedStyles,
    overridedStyles,
    getAvailabilityByWarehouse,
    availabilityByWarehouse,
    setWarehouseOverride,
    warehousesDataView,
    loadWarehouses,
    isCSOrder,
}) => {
    const { type } = useCurrentPage();
    const styles = useExpandProps(warehouseOverrideDropdownStyles, extendedStyles, overridedStyles);
    const [options, setOptions] = useState<OptionObject[]>([]);
    const cartLinesStringToCompare = React.useMemo(() => cartLines, [JSON.stringify(cartLines)]);
    const configurableChildren = useExtractCartLineConfigurableChildrenToArray(cartLinesStringToCompare);
    React.useEffect(() => {
        const defaultLat = constants.geolocation.defaultLat;
        const defaultLong = constants.geolocation.defaultLong;
        loadWarehouses({
            search: "",
            latitude: defaultLat,
            longitude: defaultLong,
            radius: 0,
            sort: "Distance",
        });
        if (fulfillmentMethod !== FulfillmentMethod.Ship && warehouseOverride) {
            setWarehouseOverride({ warehouseOverride: "" });
        }
    }, []);
    React.useEffect(() => {
        if (fulfillmentMethod !== FulfillmentMethod.Ship || !cartLines || !canViewDropdown) {
            return;
        }

        const skusString = cartLines
            .filter(line => !line.properties.productsInKit)
            .map(line => line.erpNumber)
            .concat(configurableChildren ? configurableChildren.map(item => item.erpNumber) : [])
            .join(",");
        getAvailabilityByWarehouse({ productSkus: [skusString] });
    }, [canViewDropdown, configurableChildren, cartLinesStringToCompare]);

    React.useEffect(() => {
        if (fulfillmentMethod !== FulfillmentMethod.Ship || !cartLines || !canViewDropdown) {
            return;
        }
        if (pickUpWarehouse?.name && !warehouseOverride) {
            setWarehouseOverride({ warehouseOverride: pickUpWarehouse?.name });
        }
    }, []);

    useEffect(() => {
        if (!availabilityByWarehouse) {
            return;
        }
        if (!cartLines || !canViewDropdown) {
            return;
        }
        let warehousesToUse = availabilityByWarehouse;
        if (isCSOrder) {
            warehousesToUse = warehousesToUse.filter(p =>
                constants.allowedCounterSaleWarehouseOverrides.includes(p.warehouseName),
            );
            if (!constants.allowedCounterSaleWarehouseOverrides.includes(warehouseOverride)) {
                setWarehouseOverride({ warehouseOverride: "" });
            }
        }
        const newOptions = warehousesToUse.map(w => ({
            optionText: getDescription(w.warehouseName),
            optionValue: w.warehouseName || undefined,
            rowChildren: (
                <StyledWrapper>
                    <StyledWrapper>
                        <Typography {...styles.warehouseName}>{getDescription(w.warehouseName)}</Typography>
                        <InventoryMessage color={getAvailabilityColor(w.availabilityCode)}>
                            <Typography>
                                {w.availabilityCode === "AllAvailable" && <>{translate("All Available")}</>}
                                {w.availabilityCode === "PartiallyAvailable" && <>{translate("Partially Available")}</>}
                                {w.availabilityCode === "Available" && <>{w.availabilityCode}</>}
                                {w.availabilityCode === "AvailableSoon" &&
                                    translate("Store_ProductAvailability_AvailableSoon")}
                                {w.availabilityCode === constants.availability.notAvailable &&
                                    translate("Store_ProductAvailability_NotAvailable")}
                                {w.availabilityCode === "Restricted" &&
                                    translate("Store_ProductAvailability_Restricted")}
                            </Typography>
                            &nbsp;
                            <Typography
                                {...styles.stockAsciiIcon}
                                dangerouslySetInnerHTML={{ __html: getAvailabilityIcon(w.availabilityCode) }}
                            ></Typography>
                        </InventoryMessage>
                    </StyledWrapper>
                </StyledWrapper>
            ),
        }));
        setOptions(newOptions);
    }, [warehousesDataView, availabilityByWarehouse, isCSOrder]);

    const getDescription = (name: string): string =>
        warehousesDataView.value?.find(item => item.name === name)?.description || name;

    if (
        fulfillmentMethod !== FulfillmentMethod.Ship ||
        !cartLines ||
        !canViewDropdown ||
        !availabilityByWarehouse ||
        availabilityByWarehouse.length === 0
    ) {
        return null;
    }
    if (type !== constants.pageType.checkoutReviewAndSubmit) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.label}>{translate("Warehouse Override")}:</Typography>{" "}
                {getDescription(warehouseOverride)}
            </StyledWrapper>
        );
    }
    return (
        <StyledWrapper {...styles.wrapper}>
            <DynamicDropdown
                label={translate("Warehouse Override")}
                onSelectionChange={value => {
                    if (value) {
                        setWarehouseOverride({ warehouseOverride: value });
                    } else {
                        setWarehouseOverride({ warehouseOverride: "" });
                    }
                }}
                filterOption={() => true}
                options={[
                    {
                        optionText: "No override",
                        optionValue: "",
                        rowChildren: <Typography>{translate("No override")}</Typography>,
                    },
                    ...options,
                ]}
                selected={warehouseOverride || pickUpWarehouse?.name}
                placeholder={translate("Choose a warehouse for pickup")}
                disabled={type !== constants.pageType.checkoutReviewAndSubmit}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(WarehouseOverrideDropdown);
